import React, { useEffect } from "react";
import * as yup from "yup";
import { useDispatch } from "react-redux";
import { Container, Typography, Button, Box, useTheme, useMediaQuery } from "@mui/material";
import FormTextField from "../form/FormTextField";
import FormSelect from "../form/FormSelect";
import { useForm } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import { setAlert } from "../../features/slices/alertSlice";
import { useTranslation } from "react-i18next";
import { useGetBankAccountsQuery, useGetProfileQuery, usePlayerBanksMutation } from "../../features/api/userApiSlice";
import { useNavigate } from "react-router-dom";
import { DataGrid } from "@mui/x-data-grid";

const BankAccount = () => {
  const dispatch = useDispatch();
  const { t } = useTranslation();
  const navigate = useNavigate();
  const theme = useTheme();
  const mobileView = useMediaQuery(theme.breakpoints.down("sm"));

  const {
    data: bankDetails,
    isLoading: isBankDetailsLoading,
    isError: isBankDetailsError,
    isSuccess: isBankDetailsSuccess,
    error: bankDetailsError,
  } = useGetBankAccountsQuery();

  const {
    data: user,
    isFetching: isUserFetching,
    isLoading: isUserLoading,
  } = useGetProfileQuery({ prefetch: true });

  const bankBsbEnabled = process.env.REACT_APP_BANK_BSB_ENABLED === "true";

  const passwordSchema = yup.object().shape({
    accountname: yup.string().required(t("validation.bankaccname")),
    bankcode: yup.string().required(t("validation.bankaccname")),
    bankaccount: yup.string().when("bankcode", {
      is: (bankcode) => {
        const isCrypto = bankDetails?.result?.bankConfigs?.find(
          (config) => config.bankCode === bankcode
        )?.isCrypto === "true";
        return isCrypto;
      },
      then: yup.string(),
      otherwise: yup
        .string()
        .required(t("validation.bankaccno"))
        .matches(/^[0-9]+$/, t("validation.bankaccnoonlynumbers")),
    }),
    bankbsb: bankBsbEnabled
      ? yup.string().required(t("validation.bankbsb"))
      : yup.string(),
  });


  const [playerBanks, { data, isLoading, isError, isSuccess, error }] =
    usePlayerBanksMutation();

  const bankAccountUseMobile = process.env.REACT_APP_BANK_ACCOUNT_USE_MOBILE === "true";

  const { control, handleSubmit, reset, setValue } = useForm({
    resolver: yupResolver(passwordSchema),
  });

  useEffect(() => {
    if (bankAccountUseMobile && user?.result?.mobileNumber) {
      setValue("bankaccount", user.result.mobileNumber);
    }
  }, [user?.result?.mobileNumber, bankAccountUseMobile]);

  const submitBank = async (data) => {

    let newData = {
      bankCode: data?.bankcode,
      bankAccountNumber: data?.bankaccount,
      bankAccountName: data?.accountname,
      ...(bankBsbEnabled && { bankAccountBsb: data?.bankbsb }),
    };

    await playerBanks(newData).unwrap();
    window.location.reload();
  };

  useEffect(() => {
    if (!isLoading && isSuccess) {
      dispatch(
        setAlert({
          isOpen: true,
          message: t("walletcard.bankaccountpage.successmessage"),
          variant: "success",
        })
      );
      reset();
    }
    if (isError) {
      dispatch(
        setAlert({
          isOpen: true,
          message: error.data.message,
          variant: "error",
        })
      );
    }
  }, [isLoading, isSuccess, isError]);

  return (
    <Container sx={{
      py: {
        xs: 9, sm: 0, md: 0, lg: 0
      },
    }}>
      {!mobileView && (
        <Typography fontWeight="bold" color={process.env.REACT_APP_COLOR_TEXT || "white"}>
          {t("walletcard.bankAccount")}
        </Typography>
      )}
      <Box display="flex" flexDirection="column" >
        <form onSubmit={handleSubmit(submitBank)}>
          <FormTextField
            label={t("walletcard.bankaccountpage.accountname")}
            control={control}
            controllerName="accountname"
            defaultValue={user?.result?.fullname}
            size="small"
            disabled={true}
          />
          <FormSelect
            label={t("walletcard.bankaccountpage.bankname")}
            control={control}
            controllerName="bankcode"
            items={bankDetails?.result?.bankConfigs
              .map((item) => item.bankCode)}
            size="small"
          />
          <FormTextField
            label={t("walletcard.bankaccountpage.bankaccount")}
            control={control}
            controllerName="bankaccount"
            defaultValue={""}
            size="small"
            disabled={bankAccountUseMobile}
            sx={bankAccountUseMobile ? {
              "& .MuiInputBase-input.Mui-disabled": {
                WebkitTextFillColor: process.env.REACT_APP_COLOR_DISABLE || "#9e9e9e",
              }
            } : undefined}
          />
          {bankBsbEnabled && (
            <FormTextField
              label={t("walletcard.bankaccountpage.bankbsb")}
              control={control}
              controllerName="bankbsb"
              defaultValue={""}
              size="small"
            />
          )}
          <Box alignSelf="center" mb="15px" mt="15px">
            <Button variant="contained" type="submit">
              {t("walletcard.submit")}
            </Button>
          </Box>
        </form>
      </Box>
      <Box sx={{ height: 400, width: "100%", mt: "15px" }}>
        <DataGrid
          sx={{
            '& .MuiDataGrid-cell': {
              justifyContent: 'center',
              textAlign: 'center',
            },
            '.MuiDataGrid-columnSeparator': {
              display: 'none',
            },
            '&.MuiDataGrid-root': {
              border: 'none',
            },
            "& .MuiDialogContent-root": {
              overflowY: "auto",
              "::-webkit-scrollbar": {
                width: "12px",
              },
              "::-webkit-scrollbar-track": {
                background: theme.palette.text.disabled,
              },
              "::-webkit-scrollbar-thumb": {
                background: theme.palette.background.paper,
              },
            },
            color: "white",
            backgroundColor: theme.palette.secondary.accent,
          }}
          rows={
            bankDetails?.result?.data.map((content, idx) => {
              return {
                ...content,
                id: idx + 1,
              };
            }) || []

          }
          columns={[
            {
              field: "id",
              headerName: t("walletcard.transactionpage.id"),
              width: 150,
              editable: false,
              headerAlign: 'center',
            },
            {
              field: "bankAccountName",
              headerName: t("walletcard.bankaccountpage.accountname"),
              width: 150,
              editable: false,
              headerAlign: 'center',
            },
            {
              field: "bankAccountNumber",
              headerName: t("walletcard.bankaccountpage.bankaccount"),
              width: 150,
              editable: false,
              headerAlign: 'center',
            },
            {
              field: "bankCode",
              headerName: t("walletcard.bankaccountpage.bankname"),
              width: 150,
              editable: false,
              headerAlign: 'center',
            },
            ...(bankBsbEnabled ? [{
              field: "bankAccountBsb",
              headerName: t("walletcard.bankaccountpage.bankbsb"),
              width: 150,
              editable: false,
              headerAlign: 'center',
            }] : []),
          ]}
          hideFooter={true}
          rowsPerPageOptions={[5]}
        />
      </Box>
    </Container>
  );
};

export default BankAccount;
